import { Alert, Button, Empty, Input, Modal, Table, Tag, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { apiService } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/formatApiErrorDetail';
import {
  buildQueryPayload,
  conditionsText,
  EMPTY_FORM,
  estimateText,
  formatMsk,
  hasCondition,
  isActive,
  missingDaysTexts,
  progressText,
  queryParamsOf,
  resultRows,
  STATUS_COLOR,
  STATUS_TEXT,
  wheelGate,
} from './searchTable';

// SM7 (правки заказчика 04.11). «Журналы S3 → Поиск»: внешний IP, логин, IP клиента, ресурс (домен или IP — одно поле)
// и время — любое сочетание, условия вместе. Поиск — фоновое задание службы journal-s3-index: она качает нужные сутки
// серверов из бакета и отбирает строки; результат — таблица и XLSX со всеми строками.

const pick = (response) => response?.data?.data ?? null;
const POLL_MS = 3000;
const WHEEL_STEP_MS = 250;

// Колесо над полем даты меняет значение не чаще шага WHEEL_STEP_MS: обработчик нативный и не пассивный — из onWheel
// React (пассивного) отменить прокрутку нельзя.
const useWheelThrottle = (ref) => {
  useEffect(() => {
    const node = ref.current?.input || ref.current;
    if (!node || !node.addEventListener) return undefined;
    const pass = wheelGate(WHEEL_STEP_MS);
    const onWheel = (event) => {
      if (!pass(event.timeStamp || Date.now())) event.preventDefault();
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [ref]);
};

const RESULT_COLUMNS = [
  { title: 'Начало (МСК)', dataIndex: 'started_msk', key: 'ts', width: 170 },
  { title: 'Конец (МСК)', dataIndex: 'ended_msk', key: 'last_ts', width: 170 },
  { title: 'Сервер', dataIndex: 'server', key: 'server', width: 140 },
  { title: 'Порт', dataIndex: 'port', key: 'port', width: 70 },
  { title: 'Модем', dataIndex: 'modem', key: 'modem', width: 150 },
  { title: 'Логин', dataIndex: 'login', key: 'login', width: 120 },
  { title: 'Клиент Midori (на момент)', dataIndex: 'midori_client', key: 'midori_client', width: 160 },
  { title: 'Клиент менеджера (сейчас)', dataIndex: 'manager_client', key: 'manager_client', width: 160 },
  { title: 'Внешний IP', dataIndex: 'external_ip', key: 'external_ip', width: 130, render: (value) => value || '—' },
  { title: 'IP клиента', dataIndex: 'client_ip', key: 'client_ip', width: 130 },
  { title: 'Хост', dataIndex: 'host', key: 'host', width: 180 },
  { title: 'IP ресурса', dataIndex: 'target_ip', key: 'target_ip', width: 130 },
  { title: 'Соед.', dataIndex: 'conn_count', key: 'conn_count', width: 70 },
  { title: 'Байт вх. / исх.', key: 'bytes', width: 130, render: (_, row) => `${row.bytes_in ?? '—'} / ${row.bytes_out ?? '—'}` },
  {
    title: 'Совпадение',
    key: 'match',
    width: 220,
    render: (_, row) => {
      if (row.match === 'gap') {
        return (
          <span>
            <Tag color="orange">{row.match_label}</Tag>
            <div className="sett-cell--muted">
              {`до: ${row.external_ip_before || '—'} (${formatMsk(row.external_ip_before_until)})`}
              <br />
              {`после: ${row.external_ip_after || '—'} (${formatMsk(row.external_ip_after_since)})`}
            </div>
          </span>
        );
      }
      return row.match === 'address' ? <Tag color="green">{row.match_label}</Tag> : null;
    },
  },
];

const JournalS3Search = () => {
  const location = useLocation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const sinceRef = useRef(null);
  const untilRef = useRef(null);
  useWheelThrottle(sinceRef);
  useWheelThrottle(untilRef);

  // Вход из карточки клиента (?client_id=) или ссылкой (?login=): логин прокси клиента — в поле «Логин».
  useEffect(() => {
    const { clientId, login } = queryParamsOf(location.search);
    if (login) {
      setForm((prev) => ({ ...prev, login }));
      return;
    }
    if (!clientId) return;
    apiService.lookupJournalS3Clients({ id: clientId })
      .then((response) => {
        const found = pick(response)?.clients?.[0];
        if (found) setForm((prev) => ({ ...prev, login: found.proxy_login || found.login || '' }));
      })
      .catch((e) => message.error(formatApiErrorDetail(e) || 'Клиент не найден'));
  }, [location.search]);

  const loadList = useCallback(async () => {
    try {
      setJobs(pick(await apiService.getJournalS3Queries())?.queries || []);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e) || 'Не удалось получить поиски');
    }
  }, []);

  const loadOne = useCallback(async (jobId) => {
    try {
      setSelected(pick(await apiService.getJournalS3Query(jobId)));
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'Не удалось получить результат');
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!isActive(jobs)) return undefined;
    const timer = setInterval(() => {
      loadList();
      if (selected && (selected.status === 'queued' || selected.status === 'running')) loadOne(selected.id);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [jobs, selected, loadList, loadOne]);

  const send = async (confirm) => {
    const created = pick(await apiService.createJournalS3Query(buildQueryPayload(form, confirm)));
    message.success('Поиск поставлен в очередь');
    await loadList();
    if (created) setSelected(created);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await send(false);
    } catch (e) {
      const estimate = e?.response?.status === 409 ? e.response.data?.detail?.estimate : null;
      if (estimate) {
        Modal.confirm({
          title: 'Большой объём скачивания из бакета',
          content: `${estimateText(estimate)}. Условия не сужают поиск до суток адреса или логина — читаются все `
            + 'выгруженные сутки серверов за период. Искать?',
          okText: 'Искать',
          cancelText: 'Отмена',
          onOk: () => send(true).catch((err) => message.error(formatApiErrorDetail(err) || 'Поиск не поставлен')),
        });
      } else {
        message.error(formatApiErrorDetail(e) || 'Поиск не поставлен');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadXlsx = async (job) => {
    try {
      const response = await apiService.downloadJournalS3QueryXlsx(job.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `journal_s3_search_${job.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'XLSX не получен');
    }
  };

  const listColumns = [
    { title: 'Создан (МСК)', key: 'created_at', width: 170, render: (_, row) => formatMsk(row.created_at) },
    { title: 'Условия', key: 'conditions', width: 320, render: (_, row) => conditionsText(row) },
    { title: 'Период (МСК)', key: 'window', width: 330, render: (_, row) => `${row.window_from_msk} — ${row.window_to_msk}` },
    {
      title: 'Статус',
      key: 'status',
      width: 110,
      render: (_, row) => <Tag color={STATUS_COLOR[row.status]}>{STATUS_TEXT[row.status] || row.status}</Tag>,
    },
    { title: 'Ход', key: 'progress', render: (_, row) => progressText(row) },
    {
      title: '',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <span style={{ display: 'flex', gap: 8 }}>
          <Button size="small" onClick={() => loadOne(row.id)} aria-label={`Показать поиск ${row.id}`}>Показать</Button>
          {row.status === 'done' && row.has_file ? (
            <Button size="small" onClick={() => downloadXlsx(row)} aria-label={`XLSX поиска ${row.id}`}>XLSX</Button>
          ) : null}
        </span>
      ),
    },
  ];

  const rows = useMemo(() => resultRows(selected), [selected]);
  const missing = missingDaysTexts(selected?.progress);
  const warnings = selected?.progress?.warnings || [];
  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Поиск по журналам доступа серверов Midori"
        description={
          'Условия — в любом сочетании, вместе: внешний IP, логин, IP клиента, ресурс (домен или IP одним полем; домен '
          + 'находит и поддомены). Время — московское: только «с» — эта секунда, «с» и «по» — период до 31 суток '
          + '(«по» не включается). По внешнему IP — тем же условием, что фильтр «Журнала доступа» Midori; строки окна '
          + 'смены адреса — с пометкой. Сутки D попадают в бакет после 03:15 МСК D+1 — сегодняшние строки появятся завтра.'
        }
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Внешний IP" value={form.externalIp} style={{ width: 170 }} aria-label="Внешний IP"
          onChange={field('externalIp')} />
        <Input placeholder="Логин" value={form.login} style={{ width: 150 }} aria-label="Логин" onChange={field('login')} />
        <Input placeholder="IP клиента" value={form.clientIp} style={{ width: 170 }} aria-label="IP клиента"
          onChange={field('clientIp')} />
        <Input placeholder="Ресурс: домен или IP" value={form.resource} style={{ width: 210 }}
          aria-label="Ресурс: домен или IP" onChange={field('resource')} />
        <Input ref={sinceRef} type="datetime-local" step="1" value={form.since} style={{ width: 220 }} aria-label="С (МСК)"
          onChange={field('since')} />
        <Input ref={untilRef} type="datetime-local" step="1" value={form.until} style={{ width: 220 }} aria-label="По (МСК)"
          onChange={field('until')} />
        <Button type="primary" onClick={submit} loading={submitting} disabled={!hasCondition(form) || !form.since}>
          Искать
        </Button>
        <Button onClick={loadList}>Обновить</Button>
      </div>
      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} /> : null}
      <Table
        size="small"
        rowKey="id"
        columns={listColumns}
        dataSource={jobs}
        pagination={false}
        locale={{ emptyText: <Empty description="Поисков ещё не было" /> }}
        style={{ marginBottom: 16 }}
        scroll={{ x: 1300 }}
      />
      {selected ? (
        <div>
          <h3 style={{ marginTop: 8 }}>
            {`Поиск ${selected.id}: ${conditionsText(selected)}, ${selected.window_from_msk} — ${selected.window_to_msk} (МСК)`}
          </h3>
          <div className="sett-cell--muted" style={{ marginBottom: 8 }}>
            {`${STATUS_TEXT[selected.status] || selected.status} · ${progressText(selected)}`}
          </div>
          {warnings.map((text) => <Alert key={text} type="warning" showIcon style={{ marginBottom: 8 }} message={text} />)}
          {missing.map((text) => <Alert key={text} type="warning" showIcon style={{ marginBottom: 8 }} message={text} />)}
          {selected.status === 'done' ? (
            <Table
              size="small"
              columns={RESULT_COLUMNS}
              dataSource={rows}
              pagination={{ pageSize: 50 }}
              scroll={{ x: 2300 }}
              locale={{ emptyText: <Empty description="Строк по этим условиям нет" /> }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default JournalS3Search;
